import React, { useCallback, useEffect, useState } from 'react'
import { Link, NavLink, Navigate, useParams } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import { ArrowLeftIcon, ArrowPathIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { DbStatusBadge, TENANT_TABS, TENANT_WIDE_TABS } from './tenantShared'
import BillableUsage from '../BillableUsage'
import StorageMonitoring from '../StorageMonitoring'
import TenantOverviewTab from './TenantOverviewTab'
import TenantBrandingTab from './TenantBrandingTab'
import TenantProfileSafeTab from './TenantProfileSafeTab'
import TenantPasswordTab from './TenantPasswordTab'
import { TenantPsgGatewayPanel } from '../TenantPsgGateway'
import MobileDeviceApprovals from '../MobileDeviceApprovals'
import TenantPrinters from '../TenantPrinters'
import TenantMobileConfigTab from './TenantMobileConfigTab'
import TenantDailyDataTab from './TenantDailyDataTab'
import TenantDocumentRegistryTab from './TenantDocumentRegistryTab'

export default function TenantWorkspace() {
  const { groupId, tab: tabParam } = useParams()
  const tab = tabParam || 'overview'
  const [tenant, setTenant] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadTenant = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}`))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const group = data.group || data.tenant || data.data || data
      setTenant({ ...group, venues: data.venues || group.venues || [] })
    } catch (e) {
      toast.error(e.message || 'Failed to load tenant')
      setTenant(null)
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    loadTenant()
  }, [loadTenant])

  if (!tabParam) {
    return <Navigate to={`/admin/tenants/${encodeURIComponent(groupId)}/overview`} replace />
  }

  const validTab = TENANT_TABS.some((t) => t.id === tab)
  if (!validTab) {
    return <Navigate to={`/admin/tenants/${encodeURIComponent(groupId)}/overview`} replace />
  }

  const base = `/admin/tenants/${encodeURIComponent(groupId)}`
  const wideLayout = TENANT_WIDE_TABS.has(tab)

  return (
    <div className={`${wideLayout ? 'max-w-none' : 'max-w-6xl'} mx-auto space-y-4`}>
      <Link to="/admin/tenants" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeftIcon className="h-4 w-4" />
        All tenants
      </Link>

      {loading ? (
        <p className="text-sm text-gray-500 py-8">Loading tenant…</p>
      ) : !tenant ? (
        <p className="text-sm text-red-600">Tenant not found.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 pb-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-psg-teal p-2">
                <BuildingOfficeIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{tenant.name}</h1>
                <p className="text-sm font-mono text-teal-800">{tenant.group_id}</p>
                <div className="mt-1">
                  <DbStatusBadge tenant={tenant} />
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={loadTenant}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <nav className="flex flex-wrap gap-1 border-b border-gray-200 -mb-px">
            {TENANT_TABS.map((t) => (
              <NavLink
                key={t.id}
                to={`${base}/${t.id}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    isActive ? 'border-teal-600 text-teal-800' : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>

          <div className="pt-2">
            {tab === 'overview' && <TenantOverviewTab tenant={tenant} onTenantUpdated={loadTenant} />}
            {tab === 'branding' && <TenantBrandingTab tenant={tenant} onTenantUpdated={loadTenant} />}
            {tab === 'profilesafe' && <TenantProfileSafeTab tenant={tenant} />}
            {tab === 'password' && <TenantPasswordTab tenant={tenant} />}
            {tab === 'psg-gateway' && <TenantPsgGatewayPanel groupId={groupId} embedded />}
            {tab === 'devices' && (
              <div className="-mx-4 sm:-mx-6 lg:mx-0 rounded-lg border border-gray-200 overflow-hidden min-h-[480px]">
                <MobileDeviceApprovals fixedGroupId={groupId} embedded />
              </div>
            )}
            {tab === 'mobile' && (
              <TenantMobileConfigTab groupId={groupId} venues={tenant.venues || []} />
            )}
            {tab === 'printers' && <TenantPrinters fixedGroupId={groupId} embedded />}
            {tab === 'billable-usage' && (
              <BillableUsage fixedGroupId={groupId} embedded />
            )}
            {tab === 'storage' && (
              <StorageMonitoring fixedGroupId={groupId} embedded />
            )}
            {tab === 'document-registry' && (
              <TenantDocumentRegistryTab groupId={groupId} />
            )}
            {tab === 'daily-data' && (
              <TenantDailyDataTab groupId={groupId} />
            )}
          </div>
        </>
      )}
    </div>
  )
}
